import React, { useMemo } from 'react';
import { AttendanceStats } from '../../../api/API';
import './WeeklyHeatmap.css';

interface WeeklyHeatmapProps {
    attendanceStats: AttendanceStats;
}

const COLS = 13;
const MAX_WEEKS = 52;
const CUTOFF_ISO_WEEK = '2025-W45';

function getISOWeekString(date: Date): string {
    const d = new Date(date.getTime());
    d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
    const yearStart = new Date(d.getFullYear(), 0, 4);
    yearStart.setDate(yearStart.getDate() + 3 - ((yearStart.getDay() + 6) % 7));
    const weekNo = 1 + Math.round(((d.getTime() - yearStart.getTime()) / 86400000 - 3 + ((yearStart.getDay() + 6) % 7)) / 7);
    const isoYear = d.getFullYear();
    return `${isoYear}-W${String(weekNo).padStart(2, '0')}`;
}

function getMonthLabel(date: Date): string {
    return date.toLocaleDateString('en', { month: 'short' });
}

function getIntensityClass(count: number): string {
    if (count === 0) return 'intensity-0';
    if (count === 1) return 'intensity-1';
    if (count === 2) return 'intensity-2';
    if (count <= 3) return 'intensity-3';
    return 'intensity-4';
}

function parseISOWeek(isoWeek: string): Date {
    const match = /^(\d{4})-W(\d{2})$/.exec(isoWeek);
    if (!match) return new Date();
    const year = parseInt(match[1], 10);
    const week = parseInt(match[2], 10);
    const jan4 = new Date(year, 0, 4);
    const jan4Day = (jan4.getDay() + 6) % 7;
    const mondayWeek1 = new Date(jan4);
    mondayWeek1.setDate(jan4.getDate() - jan4Day);
    const result = new Date(mondayWeek1);
    result.setDate(mondayWeek1.getDate() + (week - 1) * 7);
    return result;
}

const WeeklyHeatmap: React.FC<WeeklyHeatmapProps> = ({ attendanceStats }) => {
    const { attendedWeeks, currentStreak, streakResetWeeks } = attendanceStats;
    const gapTolerance = streakResetWeeks ?? 2;

    // Weeks in ASC order: oldest first (top-left), current week last (bottom-right).
    const weeks = useMemo(() => {
        const now = new Date();
        const currentMonday = new Date(now);
        currentMonday.setDate(now.getDate() - ((now.getDay() + 6) % 7));

        const result: { isoWeek: string; count: number; weekDate: Date }[] = [];
        for (let i = 0; i < MAX_WEEKS; i++) {
            const weekDate = new Date(currentMonday);
            weekDate.setDate(currentMonday.getDate() - i * 7);
            const isoWeek = getISOWeekString(weekDate);
            if (isoWeek < CUTOFF_ISO_WEEK) continue;
            const count = (attendedWeeks && attendedWeeks[isoWeek]) || 0;
            result.push({ isoWeek, count, weekDate });
        }
        result.reverse();
        return result;
    }, [attendedWeeks]);

    // Chunk into rows of COLS (oldest first, top-left; newest last, bottom-right)
    const rows = useMemo(() => {
        const result: typeof weeks[] = [];
        for (let i = 0; i < weeks.length; i += COLS) {
            result.push(weeks.slice(i, i + COLS));
        }
        return result;
    }, [weeks]);

    const rowMonthMarkers = useMemo(() => {
        return rows.map((row, rowIdx) => {
            const markers: { col: number; label: string }[] = [];
            for (let i = 0; i < row.length; i++) {
                const month = row[i].weekDate.getMonth();
                if (i === 0) {
                    const prevRow = rowIdx > 0 ? rows[rowIdx - 1] : null;
                    const prevMonth = prevRow ? prevRow[prevRow.length - 1].weekDate.getMonth() : -1;
                    if (month !== prevMonth) {
                        markers.push({ col: i, label: getMonthLabel(row[i].weekDate) });
                    }
                } else if (month !== row[i - 1].weekDate.getMonth()) {
                    markers.push({ col: i, label: getMonthLabel(row[i].weekDate) });
                }
            }
            return markers;
        });
    }, [rows]);

    // Active streak weeks — iterate from newest end, tolerate gaps up to streakResetWeeks
    const streakWeeks = useMemo(() => {
        if (currentStreak <= 0 || weeks.length === 0) return new Set<string>();
        const streakSet = new Set<string>();
        let gapCount = 0;
        for (let i = weeks.length - 1; i >= 0 && streakSet.size < currentStreak; i--) {
            if (weeks[i].count > 0) {
                streakSet.add(weeks[i].isoWeek);
                gapCount = 0;
            } else if (streakSet.size > 0) {
                gapCount++;
                if (gapCount >= gapTolerance) break;
            }
        }
        return streakSet;
    }, [weeks, currentStreak, gapTolerance]);

    return (
        <div className="weekly-heatmap">
            {rows.map((row, rowIdx) => (
                <div key={rowIdx} className="heatmap-row">
                    <div className="heatmap-month-labels">
                        {rowMonthMarkers[rowIdx].map(m => (
                            <span
                                key={m.col}
                                className="heatmap-month-label"
                                style={{ gridColumn: m.col + 1 }}
                            >
                                {m.label}
                            </span>
                        ))}
                    </div>
                    <div className="heatmap-row-cells">
                        {row.map(w => {
                            const isStreak = streakWeeks.has(w.isoWeek);
                            return (
                                <div
                                    key={w.isoWeek}
                                    className={`heatmap-cell ${getIntensityClass(w.count)} ${isStreak ? 'streak' : ''}`}
                                    title={`${w.isoWeek}: ${w.count} class${w.count !== 1 ? 'es' : ''}`}
                                >
                                    {w.count >= 4 ? '4+' : w.count}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
            <div className="heatmap-legend">
                <span className="heatmap-legend-label">Less</span>
                <div className="heatmap-cell intensity-0 legend-cell" />
                <div className="heatmap-cell intensity-1 legend-cell" />
                <div className="heatmap-cell intensity-2 legend-cell" />
                <div className="heatmap-cell intensity-3 legend-cell" />
                <div className="heatmap-cell intensity-4 legend-cell" />
                <span className="heatmap-legend-label">More</span>
            </div>
        </div>
    );
};

export default WeeklyHeatmap;
